import * as z from "zod";
import { cursorPagination, decimal, version } from "../index";
import { MAX_LINE_UNITS } from "../til";
import { poLineStatuses } from "./enums";
import { closureReason, purchaseOrderDto } from "./purchase-order";

// Lines of a purchase order (50-api/procurement.md §2.2; FR-M5-03, FR-M5-06).

/** A PO has up to 3,000 lines (I-02). */
export const MAX_PURCHASE_ORDER_LINES = 3000;

/** By line number. */
export const listPurchaseOrderLinesInput = z.object({
  purchaseOrderId: z.uuid(),
  status: z.enum(poLineStatuses).optional(),
  ...cursorPagination({ defaultLimit: 200, maxLimit: 500 }),
});
export type ListPurchaseOrderLinesInput = z.input<typeof listPurchaseOrderLinesInput>;
export const purchaseOrderLinesQuery = listPurchaseOrderLinesInput.omit({ purchaseOrderId: true });

const unitCost = decimal.refine((v) => Number(v) >= 0, "A cost is not negative");

/**
 * The fields of a line a save may change: an absent one stays as it is, `null` empties it. The apps check a cell with
 * the schema of its field (ADR-056).
 */
export const purchaseOrderLineFields = z.object({
  qtyOrdered: z.number().int().min(1).max(MAX_LINE_UNITS).optional(),
  unitCost: unitCost.optional(),
  expectedReceiptDate: z.iso.date().nullable().optional(),
  vendorCode: z.string().trim().min(1).max(80).nullable().optional(),
  description: z.string().trim().min(1).max(2000).nullable().optional(),
});

/** What changes on a line. `cancelled` takes it out of the order and of its totals, `open` puts it back. */
const purchaseOrderLineChange = z.object({
  id: z.uuid(),
  ...purchaseOrderLineFields.shape,
  status: z.enum(["open", "cancelled"]).optional(),
  /** Why a line is cancelled; emptied when it is opened again. */
  closureReason: closureReason.nullable().optional(),
});
export type PurchaseOrderLineChange = z.output<typeof purchaseOrderLineChange>;

/**
 * Changes to the lines of a `generated` PO, all or none. `version` is the PO's: it is the lock of the PO and of its
 * lines, and every change gives it a new one.
 */
export const savePurchaseOrderLinesInput = z.object({
  purchaseOrderId: z.uuid(),
  version,
  lines: z.array(purchaseOrderLineChange).min(1).max(MAX_PURCHASE_ORDER_LINES),
});
export type SavePurchaseOrderLinesInput = z.input<typeof savePurchaseOrderLinesInput>;
export const savePurchaseOrderLinesBody = savePurchaseOrderLinesInput.omit({ purchaseOrderId: true });

/**
 * Lines of a confirmed PO nothing more is expected on, whatever arrived (I-02), each with its reason. All or none;
 * `version` is the PO's. The PO closes with its last open line.
 */
export const closePurchaseOrderLinesInput = z.object({
  purchaseOrderId: z.uuid(),
  version,
  lines: z
    .array(z.object({ id: z.uuid(), closureReason }))
    .min(1)
    .max(MAX_PURCHASE_ORDER_LINES),
});
export type ClosePurchaseOrderLinesInput = z.input<typeof closePurchaseOrderLinesInput>;
export const closePurchaseOrderLinesBody = closePurchaseOrderLinesInput.omit({ purchaseOrderId: true });

/**
 * A line as the supplier sees it too (FR-M5-03). What comes from the catalog (codes, style, colour, size) is kept on
 * the line (ADR-168), null when the catalog did not have it yet the last time it was copied.
 */
export const purchaseOrderLineDto = z
  .object({
    id: z.uuid(),
    /** The number of the line in the PO, which every other system calls it by (I-02): never reused. */
    lineNo: z.number().int(),
    skuId: z.uuid(),
    skuCode: z.string().nullable(),
    ean: z.string().nullable(),
    styleCode: z.string().nullable(),
    styleName: z.string().nullable(),
    colour: z.string().nullable(),
    size: z.string().nullable(),
    vendorCode: z.string().nullable(),
    description: z.string().nullable(),
    /** The quantity the line was born with. */
    qtyInitial: z.number().int(),
    qtyOrdered: z.number().int(),
    qtyConfirmed: z.number().int().nullable(),
    qtyReceived: z.number().int(),
    qtyBilled: z.number().int(),
    /** In the currency of the PO. */
    unitCost: decimal.optional(),
    expectedReceiptDate: z.iso.date().nullable(),
    status: z.enum(poLineStatuses),
    closureReason: z.string().nullable(),
    isAmended: z.boolean(),
  })
  .meta({ id: "PurchaseOrderLine" });

/** The PO with its new version and totals, and the lines the save changed. */
export const savedPurchaseOrderLinesDto = z
  .object({ purchaseOrder: purchaseOrderDto, lines: z.array(purchaseOrderLineDto) })
  .meta({ id: "SavedPurchaseOrderLines" });
